import { cn } from '@/utils/tailwind-util';
// @ts-nocheck
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/solid/index';
import React, { useImperativeHandle, useRef } from 'react';

import Transition from '@/components/Transition';

export interface SelectOption<T = unknown> {
  value: T;
  label: React.ReactNode;
  hidden?: boolean;
  disabled?: boolean;
}

export interface Props extends React.SelectHTMLAttributes<unknown> {
  options: SelectOption[];
  currentOption?: SelectOption;
  onSelect: (option: SelectOption) => void;
  placeholder?: string;
  disabled?: boolean;
  activated?: boolean;
  invalid?: boolean;
  width?: string;
  height?: string;
  containerStyle?: string;
  buttonStyles?: string;
  showIcon?: boolean;
  showSelectedIcon?: boolean;
  showOptions?: boolean;
  [key: string]: unknown;
}

const DEFAULT_OPTION: SelectOption = {
  label: '선택',
  value: null,
};

const Select = React.forwardRef<HTMLSelectElement, Props>(
  (
    {
      options,
      currentOption,
      placeholder,
      disabled,
      activated,
      invalid,
      buttonStyles,
      containerStyle,
      onSelect,
      width,
      height,
      showIcon = true,
      showSelectedIcon = true,
      showOptions = true,
      ...rest
    },
    ref
  ) => {
    const innerRef = useRef(null);

    /**
     * forwardRef 로 ref를 사용하는 부모 측에서 커스터마이징된 메서드를 사용할 수 있게 해주는 Hook
     * @use
     * 완료 버튼 클릭시 에러인 경우,
     * 해당 포지션으로 스크롤만 되고 컴포넌트에 focus 되지 않는 이슈 해결하기 위함.
     * @reference
     * https://react.dev/reference/react/useImperativeHandle
     */
    useImperativeHandle(ref, () => ({
      focus: () => {
        if (innerRef?.current) {
          innerRef.current.focus();
        }
      },
    }));

    return (
      <Listbox
        ref={ref}
        value={currentOption}
        disabled={disabled}
        onChange={(option) => onSelect(option)}
      >
        <div className={cn('relative w-full', containerStyle)}>
          <Listbox.Button
            ref={innerRef}
            className={cn(
              'text-sm sm:text-base border max-h-[3rem] border-gray-400 relative w-full h-auto py-3 pl-4 pr-11 text-left bg-white rounded hover:border-primary focus:bg-primary-50 focus:border-primary focus:outline-none disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300',
              buttonStyles,
              currentOption ? 'text-gray-900' : 'text-gray-400',
              invalid && 'border-red-500',
              disabled ? 'cursor-default' : 'cursor-pointer',
              activated && 'bg-primary-50 border-primary',
              { width, height }
            )}
            {...rest}
          >
            {options.length > 0 && currentOption?.label ? (
              <span className="block truncate">{currentOption.label}</span>
            ) : (
              <span className="block truncate text-gray-400">
                {placeholder}
              </span>
            )}
            {showIcon && (
              <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDownIcon
                  aria-hidden="true"
                  className={cn(
                    'w-5 h-5',
                    disabled ? 'text-gray-400' : 'text-gray-700'
                  )}
                />
              </span>
            )}
          </Listbox.Button>

          {showOptions && (
            <Transition as={React.Fragment}>
              <Listbox.Options className="z-30 absolute w-full py-1 mt-1 overflow-auto text-base border border-gray-100 bg-white rounded shadow-lg max-h-64 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {options && options.length > 0 ? (
                  options.map((option, i) => (
                    <Listbox.Option
                      key={
                        (typeof options.value === 'string' && option.value) ||
                        `option_${i}`
                      }
                      value={option}
                      disabled={option.disabled}
                      className="select-none relative"
                    >
                      {({ active }) => {
                        const selected = option.value === currentOption?.value;

                        return (
                          <>
                            {/* option?.hidden 이 true 인 경우에만 리스트에 보여준다 */}
                            {option?.hidden &&
                            option?.hidden === true ? null : (
                              <div
                                className={cn(
                                  'px-4 py-2',
                                  option.disabled
                                    ? 'cursor-not-allowed'
                                    : 'cursor-pointer',
                                  showSelectedIcon && selected && 'pl-8',
                                  !option.disabled && (selected || active)
                                    ? 'text-primary-700 bg-primary-100'
                                    : option.disabled
                                    ? 'text-gray-400'
                                    : 'text-gray-700'
                                )}
                              >
                                <span
                                  className={cn(
                                    'block whitespace-pre-line',
                                    selected ? 'font-medium' : 'font-normal'
                                  )}
                                >
                                  {option.label}
                                </span>
                                {showSelectedIcon && selected && (
                                  <span
                                    className={cn(
                                      'absolute inset-y-0 left-0 flex items-center pl-3',
                                      active && 'text-primary-700'
                                    )}
                                  >
                                    <CheckIcon
                                      className="w-4 h-4"
                                      aria-hidden="true"
                                    />
                                  </span>
                                )}
                              </div>
                            )}
                          </>
                        );
                      }}
                    </Listbox.Option>
                  ))
                ) : (
                  <Listbox.Option
                    value={null}
                    className="py-2 px-4 text-gray-700"
                    disabled
                  >
                    데이터가 없습니다
                  </Listbox.Option>
                )}
              </Listbox.Options>
            </Transition>
          )}
        </div>
      </Listbox>
    );
  }
);

Select.defaultProps = {
  placeholder: DEFAULT_OPTION.label,
  disabled: false,
  invalid: false,
};

Select.displayName = 'Select';

export default Select;
